import { MenuItem } from "@/app/ui/navigation/types";
import type Guid from "@/app/types/common/guid";
import type { Location } from "vue-router";
import tenantStore from "@/store/modules/tenantStore";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";
import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";

export const batchMenu = {
  root: "navigation_batch_group",
  list: {
    view: "batch_list",
    plannedBatchesTab: "planned_batches",
    viewPlannedBatchesTab: (): Location => ({
      name: batchMenu.list.plannedBatchesTab,
    }),
    inProgressBatchesTab: "in_progress_batches",
    viewInProgressBatchesTab: (): Location => ({
      name: batchMenu.list.inProgressBatchesTab,
    }),
    completedBatchesTab: "completed_batches",
    viewCompletedBatchesTab: (): Location => ({
      name: batchMenu.list.completedBatchesTab,
    }),
  },
  batch: {
    view: "batch_view",
    viewBatch: (id: Guid): Location => ({
      name: batchMenu.batch.view,
      params: { id: id.toString() },
    }),
    createBatch: (): Location => ({
      name: batchMenu.batch.view,
    }),
    process: "batch_process_view",
    viewProcess: (id: Guid): Location => ({
      name: batchMenu.batch.process,
      params: { id: id.toString() },
    }),
    batchPerformance: "batch_performance",
    viewBatchPerformance: (id: Guid): Location => ({
      name: batchMenu.batch.batchPerformance,
      params: { id: id.toString() },
    }),
    processOverview: "process_overview",
    viewProcessOverview: (id: Guid): Location => ({
      name: batchMenu.batch.processOverview,
      params: { id: id.toString() },
    }),
  },
  listProcesses: "list_processes",
  recipe: {
    list: "recipe_list",
    view: "recipe_view",
    listRecipes: (): Location => ({
      name: batchMenu.recipe.list,
    }),
    viewRecipe: (id: Guid): Location => ({
      name: batchMenu.recipe.view,
      params: { id: id.toString() },
    }),
    batch_template_list: "batch_template_list",
    view_batch_template: "view_batch_template",
    listBatchTemplates: (): Location => ({
      name: batchMenu.recipe.batch_template_list,
    }),
    viewBatchTemplate: (id: Guid): Location => ({
      name: batchMenu.recipe.view_batch_template,
      params: { id: id.toString() },
    }),
    listIngredientsUsage: "list_ingredients_usage",
  },
  vesselScheduling: "batch_vessel_scheduling",
  viewVesselScheduling: (): Location => ({ name: batchMenu.vesselScheduling }),
  vesselSummary: "batch_vessel_summary",
  pickingList: "batch_picking_list",
  viewPickingList: (opts?: { processId?: Guid }): Location => ({
    name: batchMenu.pickingList,
    query: {
      processId: opts?.processId?.toString(),
    },
  }),
  agingCasks: {
    caskVessels: {
      list: "batch_cask_vessels",
      listLocation: (): Location => ({
        name: batchMenu.agingCasks.caskVessels.list,
      }),
      edit: "batch_cask_vessels_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: batchMenu.agingCasks.caskVessels.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: batchMenu.agingCasks.caskVessels.edit,
      }),
    },
  },
  batchGenealogy: "batch_genealogy",
  maintenance: {
    root: "maintenance",
    list_maintenance_recipe: "list_maintenance_recipe",
    view_maintenance_recipe: "view_maintenance_recipe",
    listMaintenanceRecipes: (): Location => ({
      name: batchMenu.maintenance.list_maintenance_recipe,
    }),
    viewMaintenanceRecipe: (id: Guid): Location => ({
      name: batchMenu.maintenance.view_maintenance_recipe,
      params: { id: id.toString() },
    }),
    vessel_records: "vessel_records",
    maintenance_schedule: "maintenance_schedule",
  },
};

export const buildBatchMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: batchMenu.root,
    icon: "LocalDrink",
    visible: () =>
      hasFeatureUsingUserStore(EnumFeatureFlag.onlyDev) ||
      tenantStore.state.tenantSettings.isMegaBatchesEnabled,
    subMenus: [
      MenuItem.fromOpts({
        name: batchMenu.vesselScheduling,
        url: "/batch/vessel-scheduling",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/scheduling/VesselScheduling.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.vesselSummary,
        url: "/batch/vessels",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/vesselSummary/VesselSummary.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.list.view,
        url: "/batch/list",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/ListBatches.vue"
          ),
        redirect: "/batch/list/in-progress-batches",
        requiredUserPermissions: [EnumUserPermission.batchesView],
        childRoutes: [
          {
            name: batchMenu.list.plannedBatchesTab,
            path: "/batch/list/planned-batches",
            component: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/PlannedBatchesTab.vue"
              ),
          },
          {
            name: batchMenu.list.inProgressBatchesTab,
            path: "/batch/list/in-progress-batches",
            component: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/InProgressBatchesTab.vue"
              ),
          },
          {
            name: batchMenu.list.completedBatchesTab,
            path: "/batch/list/complete-batches",
            component: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/CompletedBatchesTab.vue"
              ),
          },
        ],
      }),
      MenuItem.fromOpts({
        name: batchMenu.batch.view,
        url: "/batch/view-batch/:id",
        breadcrumbParentName: batchMenu.list.inProgressBatchesTab,
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/overview/ViewBatch.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
        redirect: "/batch/batch-performance/:id",
        requiredUserPermissions: [EnumUserPermission.batchesView],
        childRoutes: [
          {
            name: batchMenu.batch.batchPerformance,
            path: "/batch/batch-performance/:id",
            component: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/overview/BatchPerformance.vue"
              ),
            props: (route) => ({
              id: route.params.id,
            }),
          },
          {
            name: batchMenu.batch.processOverview,
            path: "/batch/process-overview/:id",
            component: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/overview/ProcessOverview.vue"
              ),
            props: (route) => ({
              id: route.params.id,
            }),
          },
        ],
      }),
      MenuItem.fromOpts({
        name: batchMenu.batch.process,
        url: "/batch/process/:id",
        breadcrumbParentName: batchMenu.list.inProgressBatchesTab,
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/ViewProcess.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.listProcesses,
        url: "/batch/list-processes",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/pages/production/ListProcessesPage.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.recipe.batch_template_list,
        url: "/batch/recipe/batch-template-list",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/batchTemplates/pages/BatchTemplateList.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.recipe.view_batch_template,
        url: "/batch/recipe/view-batch-template/:id",
        breadcrumbParentName: batchMenu.recipe.batch_template_list,
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/batchTemplates/pages/ViewBatchTemplate.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.recipe.list,
        url: "/batch/recipe/list",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/recipes/ListRecipes.vue"
          ),
        componentProps: () => ({
          processTypes: [
            EnumAssemblyType.materialProduction,
            EnumAssemblyType.productPackaging,
          ],
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.recipe.view,
        url: "/batch/recipe/view-recipe/:id",
        breadcrumbParentName: batchMenu.recipe.list,
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/recipes/ViewRecipe.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.recipe.listIngredientsUsage,
        url: "/batch/recipe/list-ingredients-usage",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/recipes/ListRecipeIngredientsUsagePage.vue"
          ),
        componentProps: () => ({
          processTypes: [
            EnumAssemblyType.materialProduction,
            EnumAssemblyType.productPackaging,
          ],
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: batchMenu.pickingList,
        url: "/batch/batch-picking-list",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/batches/pickingList/BatchPickingListPage.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: batchMenu.agingCasks.caskVessels.list,
        url: "/batch/cask-vessels/view",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/production/caskVessels/ListCaskVessels.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: batchMenu.agingCasks.caskVessels.edit,
        url: "/batch/cask-vessel/:id?/:mode?",
        breadcrumbParentName: batchMenu.agingCasks.caskVessels.list,
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/production/caskVessels/EditCaskVessel.vue"
          ),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: batchMenu.batchGenealogy,
        url: "/batch/batch-genealogy/view",
        view: () =>
          import(
            /* webpackChunkName: "batch" */ "@/components/pages/production/genealogy/BatchGenealogy.vue"
          ),
      }),
      MenuItem.forBranch({
        name: batchMenu.maintenance.root,
        subMenus: [
          MenuItem.fromOpts({
            name: batchMenu.maintenance.list_maintenance_recipe,
            url: "/batch/maintenance/list-maintenance-recipe",
            view: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/recipes/ListRecipes.vue"
              ),
            componentProps: () => ({
              processTypes: [EnumAssemblyType.maintenance],
            }),
            requiredUserPermissions: [EnumUserPermission.recipesView],
          }),
          MenuItem.fromOpts({
            name: batchMenu.maintenance.view_maintenance_recipe,
            url: "/batch/maintenance/view-maintenance-recipe/:id",
            breadcrumbParentName: batchMenu.maintenance.list_maintenance_recipe,
            view: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/recipes/ViewRecipe.vue"
              ),
            componentProps: (route) => ({
              id: route.params.id,
            }),
            visible: false,
            requiredUserPermissions: [EnumUserPermission.recipesView],
          }),
          MenuItem.fromOpts({
            name: batchMenu.maintenance.vessel_records,
            url: "/production/vessels/records",
            view: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/production/vessels/VesselRecord.vue"
              ),
            requiredUserPermissions: [EnumUserPermission.vesselsView],
          }),
          MenuItem.fromOpts({
            name: batchMenu.maintenance.maintenance_schedule,
            url: "/batch/maintenance/maintenance-schedule",
            view: () =>
              import(
                /* webpackChunkName: "batch" */ "@/components/pages/batches/maintenance/ListMaintenanceSchedulePage.vue"
              ),
            requiredUserPermissions: [EnumUserPermission.vesselsView],
          }),
        ],
      }),
    ],
  }),
];
